/**
 * Batch 2 Part A - Pixel frontend injector.
 *
 * Responsibilities:
 *   1. On app mount, fetch /api/pixels/active once and cache in memory.
 *   2. Inject each provider's base pixel snippet (Meta fbq / X twq) into
 *      the document head exactly once. Google / TikTok are schema-only
 *      per Task 1a - silently ignored here.
 *   3. Expose a `trackEvent(name, payload)` API that fans out to enabled
 *      pixels whose `events` allow-list contains the event name.
 *   4. Debug logging behind ?pixel_debug=1 - each fire logs
 *      [pixel] provider event payload.
 *
 * Safe no-op if /active fails, returns empty, or if the vendor library
 * (fbq / twq) refuses to initialize. Never breaks the wrapping tree.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';

const PixelContext = createContext({
  pixels: [],
  trackEvent: () => {},
  setUserMatchData: () => {},
  ready: false,
});

const INJECTED = new Set(); // pixel_id -> already injected

function isDebug() {
  try {
    return new URLSearchParams(window.location.search).get('pixel_debug') === '1';
  } catch (_) { return false; }
}

function debugLog(...args) {
  if (isDebug()) {
    console.log('[pixel]', ...args);
  }
}

// ---------------------------------------------------------------------------
// Meta - fbq loader + init
// ---------------------------------------------------------------------------
function ensureMetaFbq() {
  if (typeof window === 'undefined') return null;
  if (window.fbq) return window.fbq;
  // Standard Meta pixel bootstrap. Verbatim from Meta docs.
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  return window.fbq || null;
}

function initMetaPixel(pixel, matchData) {
  const fbq = ensureMetaFbq();
  if (!fbq) return false;
  const key = `meta:${pixel.pixel_id}`;
  const md = normalizeMatchDataForFbq(matchData);
  try {
    // Meta merges advanced-matching data on repeat init calls, so we
    // re-init on user login to attach new keys. Bundle 84 Task 1
    // fix: never re-init the same pixel twice per lifetime unless
    // fresh advanced-matching data actually arrived, so page refresh
    // does not double-fire the base init.
    if (md && Object.keys(md).length > 0 && !INJECTED.has(key)) {
      fbq('init', pixel.pixel_id, md);
    } else if (!INJECTED.has(key)) {
      fbq('init', pixel.pixel_id);
    }
    INJECTED.add(key);
    debugLog('meta init', pixel.pixel_id, 'keys=', md ? Object.keys(md) : []);
    return true;
  } catch (e) {
    debugLog('meta init failed', pixel.pixel_id, e);
    return false;
  }
}

// Meta wants the client-side init object with keys em/ph/fn/ln/db/ge/
// ct/st/zp/country/external_id. fbevents.js auto-hashes plaintext; if a
// value is already a 64-char SHA-256 hex string (from our returning
// visitor cache) it is passed through untouched so fbq doesn't
// double-hash.
const HEX64 = /^[a-f0-9]{64}$/i;

function normalizeMatchDataForFbq(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const alias = {
    email: 'em', phone: 'ph', first_name: 'fn', last_name: 'ln',
    dob: 'db', date_of_birth: 'db', gender: 'ge',
    city: 'ct', state: 'st', zip: 'zp', postal_code: 'zp',
  };
  const norm = {};
  Object.entries(raw).forEach(([k, v]) => {
    if (v == null) return;
    const key = alias[k] || k;
    // Preserve pre-hashed SHA-256 hex values verbatim.
    if (typeof v === 'string' && HEX64.test(v)) {
      norm[key] = v;
      return;
    }
    // external_id is a stable identifier per user; keep case + trim only.
    if (key === 'external_id') {
      const val = String(v).trim();
      if (val) norm[key] = val;
      return;
    }
    let val = String(v).trim().toLowerCase();
    if (!val) return;
    if (key === 'ph') val = val.replace(/\D+/g, '');
    else if (key === 'fn' || key === 'ln' || key === 'ct' || key === 'st') {
      val = val.replace(/[^a-z0-9]/g, '');
    } else if (key === 'db') {
      const m = val.match(/^(\d{4})-?(\d{2})-?(\d{2})$/);
      val = m ? `${m[1]}${m[2]}${m[3]}` : '';
    } else if (key === 'ge') {
      if (['m', 'male', 'man'].includes(val)) val = 'm';
      else if (['f', 'female', 'woman'].includes(val)) val = 'f';
      else val = '';
    } else if (key === 'zp') {
      const zmatch = val.match(/^\d{5}/);
      if (zmatch) val = zmatch[0];
    } else if (key === 'country') {
      const map = { usa: 'us', 'united states': 'us', canada: 'ca', uk: 'gb', 'united kingdom': 'gb', india: 'in' };
      val = map[val] || val;
      if (val.length !== 2) val = '';
    }
    if (val) norm[key] = val;
  });
  return norm;
}

// Fires a Meta PageView across ALL initialized fbq pixels. Meta's SDK
// automatically fans a `track` call to every pixel registered via `init`,
// so we only need a single call per route.
function fireMetaPageView() {
  const fbq = window.fbq;
  if (!fbq) return false;
  try {
    fbq('track', 'PageView');
    return true;
  } catch (e) {
    debugLog('meta PageView fire failed', e);
    return false;
  }
}

// Meta standard events (2026). Anything outside this set is routed to
// `trackCustom` so Meta accepts the event with the exact name we supply.
const META_STANDARD_EVENTS = new Set([
  'AddPaymentInfo', 'AddToCart', 'AddToWishlist', 'CompleteRegistration',
  'Contact', 'CustomizeProduct', 'Donate', 'FindLocation',
  'InitiateCheckout', 'Lead', 'PageView', 'Purchase', 'Schedule',
  'Search', 'StartTrial', 'SubmitApplication', 'Subscribe', 'ViewContent',
]);

function fireMeta(pixel, event, payload) {
  const fbq = window.fbq;
  if (!fbq) return false;
  try {
    const method = META_STANDARD_EVENTS.has(event) ? 'track' : 'trackCustom';
    fbq(method, event, payload || {});
    debugLog('meta fire', method, event, payload || {});
    return true;
  } catch (e) {
    debugLog('meta fire failed', pixel.pixel_id, event, e);
    return false;
  }
}

// ---------------------------------------------------------------------------
// X - twq loader + init
// ---------------------------------------------------------------------------
function ensureXTwq() {
  if (typeof window === 'undefined') return null;
  if (window.twq) return window.twq;
  // Standard X (Twitter) pixel bootstrap. Verbatim from X docs.
  (function (e, t, n, s, u, a) {
    e.twq || (s = e.twq = function () {
      s.exe ? s.exe.apply(s, arguments) : s.queue.push(arguments);
    }, s.version = '1.1', s.queue = [], u = t.createElement(n), u.async = !0,
      u.src = 'https://static.ads-twitter.com/uwt.js',
      a = t.getElementsByTagName(n)[0], a.parentNode.insertBefore(u, a));
  })(window, document, 'script');
  return window.twq || null;
}

function initXPixel(pixel) {
  const twq = ensureXTwq();
  if (!twq) return false;
  if (INJECTED.has(`x:${pixel.pixel_id}`)) return true;
  try {
    twq('config', pixel.pixel_id);
    INJECTED.add(`x:${pixel.pixel_id}`);
    debugLog('x init', pixel.pixel_id);
    return true;
  } catch (e) {
    debugLog('x init failed', pixel.pixel_id, e);
    return false;
  }
}

function fireX(pixel, event, payload) {
  const twq = window.twq;
  if (!twq) return false;
  try {
    twq('event', event, payload || {});
    return true;
  } catch (e) {
    debugLog('x fire failed', pixel.pixel_id, event, e);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Anon identity + session + UTM capture
// ---------------------------------------------------------------------------
const ANON_KEY = 'dost_anon_id';
const SESSION_KEY = 'dost_session_id';
const SESSION_TS_KEY = 'dost_session_ts';
const SESSION_UTM_KEY = 'dost_session_utm';
const SESSION_FIRSTTOUCH_KEY = 'dost_session_first_touch';
const SESSION_IDLE_MS = 30 * 60 * 1000;
// Custom store for form-supplied match data (RSVP wizard, checkout, etc).
const MATCH_DATA_KEY = 'dost_pixel_match_data';
// Long-lived cache of the last authenticated user's SHA-256 hashed
// PII. Populated on login, read on subsequent mounts BEFORE the user
// logs back in. Never contains plaintext PII. Cleared on logout.
const LAST_PII_KEY = 'dost_last_pii_hashes_v1';
const LAST_PII_TS_KEY = 'dost_last_pii_hashes_v1_ts';
const LAST_PII_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
// Bundle 64 - legacy key names that predate versioning. On boot we
// migrate any surviving entries under the new versioned keys so the
// 30-day sliding TTL restarts cleanly.
const LEGACY_LAST_PII_KEYS = ['dost_last_pii_hashes'];
const LEGACY_LAST_PII_TS_KEYS = ['dost_last_pii_hashes_ts'];

// Bundle 64 - sticky PII cache write on ANY captured PII, not just
// login. RSVP wizard, checkout, profile edits - all call
// setUserMatchData which now ALSO persists to the sticky localStorage
// cache. Meta CAPI Advanced Matching coverage was 3-4% on prod because
// most fires happen pre-auth; sticking captured PII into localStorage
// (as SHA-256 hashes, never plaintext) lifts coverage for returning
// visitors and post-form guests.
function migrateLegacyPIICacheKeys() {
  try {
    if (!window.localStorage) return;
    if (window.localStorage.getItem(LAST_PII_KEY)) return;
    for (let i = 0; i < LEGACY_LAST_PII_KEYS.length; i += 1) {
      const oldK = LEGACY_LAST_PII_KEYS[i];
      const oldTsK = LEGACY_LAST_PII_TS_KEYS[i];
      const raw = window.localStorage.getItem(oldK);
      const ts = window.localStorage.getItem(oldTsK);
      if (raw) {
        window.localStorage.setItem(LAST_PII_KEY, raw);
        if (ts) window.localStorage.setItem(LAST_PII_TS_KEY, ts);
        window.localStorage.removeItem(oldK);
        if (ts) window.localStorage.removeItem(oldTsK);
        return;
      }
    }
  } catch (_) { /* noop */ }
}

async function sha256Hex(input) {
  try {
    if (!input) return null;
    const enc = new TextEncoder().encode(String(input));
    const buf = await window.crypto.subtle.digest('SHA-256', enc);
    const bytes = new Uint8Array(buf);
    let hex = '';
    for (let i = 0; i < bytes.length; i += 1) {
      hex += bytes[i].toString(16).padStart(2, '0');
    }
    return hex;
  } catch (_) { return null; }
}

function normalizePlaintextForHash(key, raw) {
  if (raw == null) return null;
  let v = String(raw).trim().toLowerCase();
  if (!v) return null;
  if (key === 'ph') {
    v = v.replace(/\D+/g, '');
  } else if (key === 'fn' || key === 'ln' || key === 'ct' || key === 'st') {
    v = v.replace(/[\s\-'".,]+/g, '');
  } else if (key === 'db') {
    const m = v.match(/^(\d{4})-?(\d{2})-?(\d{2})$/);
    v = m ? `${m[1]}${m[2]}${m[3]}` : '';
  } else if (key === 'ge') {
    if (['m', 'male', 'man'].includes(v)) v = 'm';
    else if (['f', 'female', 'woman'].includes(v)) v = 'f';
    else v = '';
  } else if (key === 'zp') {
    const zmatch = v.match(/^\d{5}/);
    if (zmatch) v = zmatch[0];
  } else if (key === 'country') {
    const map = { usa: 'us', 'united states': 'us', canada: 'ca', uk: 'gb', 'united kingdom': 'gb', india: 'in' };
    v = map[v] || v;
    if (v.length !== 2) v = '';
  }
  return v || null;
}

async function persistLastKnownPIIHashes(plaintext) {
  try {
    if (!plaintext || typeof plaintext !== 'object') return null;
    const alias = {
      email: 'em', phone: 'ph', first_name: 'fn', last_name: 'ln',
      dob: 'db', date_of_birth: 'db', gender: 'ge',
      city: 'ct', state: 'st', zip: 'zp', postal_code: 'zp',
      country: 'country',
    };
    const hashed = {};
    for (const [k, raw] of Object.entries(plaintext)) {
      const key = alias[k] || k;
      if (!['em', 'ph', 'fn', 'ln', 'db', 'ge', 'ct', 'st', 'zp', 'country'].includes(key)) continue;
      const norm = normalizePlaintextForHash(key, raw);
      if (!norm) continue;
      const h = await sha256Hex(norm);
      if (h) hashed[key] = h;
    }
    if (Object.keys(hashed).length === 0) return null;
    window.localStorage.setItem(LAST_PII_KEY, JSON.stringify(hashed));
    window.localStorage.setItem(LAST_PII_TS_KEY, String(Date.now()));
    return hashed;
  } catch (_) { return null; }
}

function readLastKnownPIIHashes() {
  try {
    const ts = parseInt(window.localStorage.getItem(LAST_PII_TS_KEY) || '0', 10);
    if (!ts || Date.now() - ts > LAST_PII_MAX_AGE_MS) return {};
    const raw = window.localStorage.getItem(LAST_PII_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_) { return {}; }
}

function clearLastKnownPIIHashes() {
  try {
    window.localStorage.removeItem(LAST_PII_KEY);
    window.localStorage.removeItem(LAST_PII_TS_KEY);
  } catch (_) { /* noop */ }
}

function readCookie(name) {
  try {
    const parts = document.cookie.split(';').map((c) => c.trim());
    for (const p of parts) {
      if (p.startsWith(`${name}=`)) return decodeURIComponent(p.slice(name.length + 1));
    }
  } catch (_) { /* noop */ }
  return null;
}

function writeCookie(name, value, days = 90) {
  try {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 3600 * 1000);
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/; SameSite=Lax`;
  } catch (_) { /* noop */ }
}

// Meta pixel's own bootstrap sets `_fbp`. We only synthesize `_fbc` from
// a landing-page `?fbclid=` when Meta hasn't already dropped it.
function ensureFbcCookie() {
  try {
    if (readCookie('_fbc')) return readCookie('_fbc');
    const p = new URLSearchParams(window.location.search);
    const fbclid = p.get('fbclid');
    if (!fbclid) return null;
    const val = `fb.1.${Date.now()}.${fbclid}`;
    writeCookie('_fbc', val, 90);
    return val;
  } catch (_) { return null; }
}

function getMatchDataSnapshot() {
  try {
    const raw = window.sessionStorage.getItem(MATCH_DATA_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_) { return {}; }
}

// Bundle 64 - merged match-data snapshot for every fire path.
// Order of precedence (most-specific wins):
//   1. sessionStorage snapshot (form values captured this session)
//   2. sticky localStorage cache (SHA-256 hashes, up to 30 days old)
// This means an anon user who has EVER logged in gets em/ph/fn/ln/etc
// re-attached to their subsequent trackEvent calls WITHOUT logging
// back in - which is the whole point of the Bundle 64 lift from
// 3.5% -> 40%+ email hash coverage on Meta CAPI.
function getMergedMatchDataForFire() {
  const sticky = readLastKnownPIIHashes() || {};
  const session = getMatchDataSnapshot() || {};
  // Session overrides sticky (fresh form values beat stale cache).
  return { ...sticky, ...session };
}

function persistMatchData(next) {
  try {
    const merged = { ...getMatchDataSnapshot(), ...(next || {}) };
    // Drop empty strings so we don't overwrite good data with blanks.
    Object.keys(merged).forEach((k) => {
      if (merged[k] == null || merged[k] === '') delete merged[k];
    });
    window.sessionStorage.setItem(MATCH_DATA_KEY, JSON.stringify(merged));
    return merged;
  } catch (_) { return next || {}; }
}

function getOrCreateId(store, key) {
  try {
    let v = store.getItem(key);
    if (!v) {
      v = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      store.setItem(key, v);
    }
    return v;
  } catch (_) { return null; }
}

function getAnonymousId() {
  return getOrCreateId(window.localStorage, ANON_KEY);
}

function getSessionId() {
  try {
    const now = Date.now();
    const ts = parseInt(window.sessionStorage.getItem(SESSION_TS_KEY) || '0', 10);
    if (!ts || now - ts > SESSION_IDLE_MS) {
      window.sessionStorage.removeItem(SESSION_KEY);
    }
    const id = getOrCreateId(window.sessionStorage, SESSION_KEY);
    window.sessionStorage.setItem(SESSION_TS_KEY, String(now));
    return id;
  } catch (_) { return null; }
}

function captureUtmOnce() {
  try {
    const cached = window.sessionStorage.getItem(SESSION_UTM_KEY);
    if (cached) return JSON.parse(cached);
    const p = new URLSearchParams(window.location.search);
    const out = {
      utm_source: p.get('utm_source'),
      utm_medium: p.get('utm_medium'),
      utm_campaign: p.get('utm_campaign'),
      utm_content: p.get('utm_content'),
      utm_term: p.get('utm_term'),
      fbclid: p.get('fbclid'),
      twclid: p.get('twclid'),
      gclid: p.get('gclid'),
    };
    window.sessionStorage.setItem(SESSION_UTM_KEY, JSON.stringify(out));
    return out;
  } catch (_) { return {}; }
}

const EVENT_BUFFER = [];
let FLUSH_TIMER = null;
const FLUSH_INTERVAL_MS = 2000;
const FLUSH_MAX = 10;

async function flushLog(useBeacon = false) {
  if (!EVENT_BUFFER.length) return;
  const events = EVENT_BUFFER.splice(0, EVENT_BUFFER.length);
  const body = JSON.stringify({ events });
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/pixels/log`;
  try {
    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
    await fetch(url, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body, keepalive: true, credentials: 'include',
    });
  } catch (_) { /* first-party analytics is best-effort */ }
}

function scheduleFlush() {
  if (FLUSH_TIMER) return;
  FLUSH_TIMER = setTimeout(() => {
    FLUSH_TIMER = null;
    flushLog(false);
  }, FLUSH_INTERVAL_MS);
}

function bufferLogEvent(event, payload, extras) {
  try {
    const utm = captureUtmOnce() || {};
    const firstTouch = window.sessionStorage.getItem(SESSION_FIRSTTOUCH_KEY) || null;
    // Bundle 64 - merge the sticky PII localStorage cache into every
    // beacon so the server-side CAPI mirror gets pre-hashed
    // em/ph/fn/ln/etc even when the user is unauthenticated. The
    // sticky cache is SHA-256 hashed at write time; server accepts
    // pre-hashed values verbatim via normalize_match_data.
    const md = { ...getMergedMatchDataForFire(), ...((extras && extras.user_data) || {}) };
    const fbp = readCookie('_fbp');
    const fbc = readCookie('_fbc') || ensureFbcCookie();
    const row = {
      event_name: event,
      payload: payload || {},
      url: window.location.href,
      path: window.location.pathname,
      referrer: document.referrer || null,
      session_id: getSessionId(),
      anonymous_id: getAnonymousId(),
      session_first_touch_source: firstTouch,
      // Meta Advanced Matching (server hashes + forwards to CAPI).
      user_data: Object.keys(md).length ? md : null,
      fbp: fbp || null,
      fbc: fbc || null,
      ...utm,
    };
    EVENT_BUFFER.push(row);
    if (EVENT_BUFFER.length >= FLUSH_MAX) flushLog(false);
    else scheduleFlush();
  } catch (_) { /* noop */ }
}

if (typeof window !== 'undefined' && !window.__dostPixelUnloadHook) {
  window.__dostPixelUnloadHook = true;
  window.addEventListener('beforeunload', () => flushLog(true));
  window.addEventListener('pagehide', () => flushLog(true));
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export const PixelProvider = ({ children }) => {
  const [pixels, setPixels] = useState([]);
  const [ready, setReady] = useState(false);
  const bootedRef = useRef(false);
  const location = useLocation();
  const { user } = useAuth();
  // Bundle 84 Task 1 - private routes MUST NOT initialize Meta Pixel
  // or fire any analytics. Survey answers + signed tokens must never
  // reach ad-tracking infrastructure.
  const isPrivateRoute = (
    (location.pathname || '').startsWith('/survey/')
    || (location.pathname || '').startsWith('/connection-followups/')
  );
  // Admin sessions (or navigation to /admin/* routes) MUST NOT pollute
  // pixel analytics. This flag short-circuits every fire path below.
  const isAdminEvent = isPrivateRoute || (
    (user && (user.role === 'admin' || user.role === 'super_admin')) ||
    (location.pathname || '').startsWith('/admin/') ||
    location.pathname === '/admin'
  );
  // Track the pathname the initial PageView was fired for at pixel init
  // so the route-change effect doesn't double-fire on first mount.
  const lastPageViewPathRef = useRef(null);

  // Meta Advanced Matching: build the plaintext user_data snapshot from
  // the authed user's profile. RSVP + checkout forms can supplement via
  // setUserMatchData(). fbq is auto-hashed by Meta on init.
  //
  // Part 5 - Meta coverage gap fix:
  //   external_id is populated ALWAYS - user.id when authed, else the
  //   long-lived anonymous_id. Meta considers external_id the single
  //   biggest lift (4.24% median conversion), and we were previously
  //   sending it only for authed sessions (0% coverage on anon).
  const authedMatchData = useMemo(() => {
    const external = user?.id || getAnonymousId();
    if (!user) {
      return external ? { external_id: external } : {};
    }
    const p = user.profile || {};
    const loc = p.location || {};
    return {
      email: user.email || undefined,
      phone: user.phone || undefined,
      first_name: user.first_name || undefined,
      last_name: user.last_name || undefined,
      gender: p.gender || undefined,
      city: loc.city || undefined,
      state: loc.state || undefined,
      country: loc.country || undefined,
      dob: p.dob || p.date_of_birth || undefined,
      external_id: external,
    };
  }, [user]);

  const setUserMatchData = useCallback((partial) => {
    if (!partial) return;
    persistMatchData(partial);
    // Bundle 64 - ALSO persist to the sticky localStorage cache so
    // form-captured PII (RSVP wizard, checkout name/email/phone)
    // survives the session and lifts Advanced Matching coverage on
    // future anon visits.
    persistLastKnownPIIHashes(partial);
  }, []);

  useEffect(() => {
    // StrictMode double-invokes this effect. `bootedRef` guarantees the
    // fetch + inject runs exactly once for the lifetime of the tab.
    // Do NOT gate the async body on a per-invocation `cancelled` flag:
    // the cleanup from the first invocation would flip it before the
    // fetch resolves.
    if (bootedRef.current) return;
    // Bundle 84 Task 1 - abort pixel bootstrap on private routes so
    // fbq / twq never even initialize when a survey token is loaded.
    if (isPrivateRoute) {
      bootedRef.current = true;
      return;
    }
    bootedRef.current = true;
    // Bundle 64 - migrate any legacy sticky-PII cache entries into the
    // versioned keys before we read them below.
    migrateLegacyPIICacheKeys();
    // Seed the _fbc cookie from ?fbclid= on first landing.
    ensureFbcCookie();
    // Seed the persisted match data with the current authed snapshot.
    persistMatchData(authedMatchData);
    (async () => {
      try {
        const { data } = await api.get('/pixels/active');
        const list = Array.isArray(data?.pixels) ? data.pixels : [];
        setPixels(list);
        debugLog('active pixels loaded', list.length, list);
        // Part 5 - Meta coverage: for the initial fbq init on an anon
        // page load, hydrate from the last-known PII hash cache so a
        // returning visitor who's ever been logged in gets PII match
        // on ViewContent + PageView BEFORE they sign back in this
        // session. Cache is SHA-256 hashes only (never plaintext).
        const anonBootData = { ...getMatchDataSnapshot() };
        if (!user) {
          const cached = readLastKnownPIIHashes();
          if (cached && Object.keys(cached).length > 0) {
            // fbq honours already-hashed values (64-char hex).
            Object.assign(anonBootData, cached);
            debugLog('meta init hydrated from PII hash cache', Object.keys(cached));
          }
        }
        // Inject client-side bootstrap for every LIVE-provider pixel.
        list.forEach((p) => {
          if (p.provider === 'meta') initMetaPixel(p, anonBootData);
          else if (p.provider === 'x') initXPixel(p);
          // google / tiktok are schema-only per Task 1a; skip.
        });
      } catch (e) {
        debugLog('failed to load /pixels/active', e?.message || e);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  // Persist the authed user's PII hashes to localStorage so a returning
  // anon session (private window closed, cookies wiped, etc.) still
  // gets PII match keys attached to Meta events. Cleared on logout.
  useEffect(() => {
    if (!user) {
      clearLastKnownPIIHashes();
      return;
    }
    // Snapshot the plaintext PII fields we care about and hash them
    // asynchronously; failure is silent (best-effort analytics).
    persistLastKnownPIIHashes(authedMatchData);
  }, [user, authedMatchData]);

  // Re-init Meta pixels whenever authenticated user identity changes.
  // Meta's fbq(init) merges advanced-matching keys, so this attaches
  // fresh em/ph/fn/ln/etc without wiping the prior anon session.
  useEffect(() => {
    if (!ready || pixels.length === 0) return;
    if (isAdminEvent) return;
    const md = persistMatchData(authedMatchData);
    pixels.forEach((p) => {
      if (p.provider === 'meta') initMetaPixel(p, md);
    });
  }, [ready, pixels, authedMatchData, isAdminEvent]);

  const trackEvent = useCallback((event, payload, extraMatchData) => {
    if (!event) return;
    if (isAdminEvent) {
      debugLog('skip trackEvent (admin session/route)', event);
      return;
    }
    // If the caller supplied event-time user data (e.g. RSVP form values),
    // persist it BEFORE firing so the fbq init picks it up too.
    if (extraMatchData) persistMatchData(extraMatchData);
    // Bundle 24 - regression sentinel. external_id is Meta's biggest EMQ
    // lift (~4.24% median CVR) and Bundle 9 Part 5 promised 100% coverage.
    // If it's ever missing on a live fire we want a LOUD warning in the
    // browser console so QA / support catches it before Meta EMQ does.
    try {
      const snapshot = getMatchDataSnapshot();
      if (!snapshot || !snapshot.external_id) {
        console.warn(
          '[pixel] trackEvent fired without external_id - Bundle 9 Part 5 regression',
          { event, snapshot_keys: Object.keys(snapshot || {}) },
        );
      }
    } catch (_) { /* noop */ }
    debugLog('trackEvent', event, payload || {});
    pixels.forEach((p) => {
      const allow = Array.isArray(p.events) ? p.events : [];
      if (allow.length && !allow.includes(event)) {
        debugLog('skip (not in allow-list)', p.provider, p.pixel_id, event);
        return;
      }
      if (p.provider === 'meta') fireMeta(p, event, payload);
      else if (p.provider === 'x') fireX(p, event, payload);
    });
    // Beacon the same event server-side so CAPI can mirror it with the
    // hashed user_data. The event_id is derived on the server, so the
    // client-side fbq and the server-side CAPI post dedupe correctly.
    bufferLogEvent(event, payload, { user_data: extraMatchData || null });
  }, [pixels, isAdminEvent]);

  // Fire Meta PageView on initial pixel load and on every client-side
  // route change. React Router does not trigger a full page load, so
  // without this the base `PageView` only ever fires for the entry URL
  // (and Meta Events Manager reports "0% complete").
  useEffect(() => {
    if (!ready) return;
    if (isAdminEvent) {
      debugLog('skip PageView (admin session/route)', location.pathname);
      return;
    }
    // Only fire if there's at least one Meta pixel whose allow-list
    // includes PageView (empty allow-list means "allow all").
    const hasMetaPageView = pixels.some((p) => {
      if (p.provider !== 'meta') return false;
      const allow = Array.isArray(p.events) ? p.events : [];
      return allow.length === 0 || allow.includes('PageView');
    });
    if (!hasMetaPageView) return;
    if (lastPageViewPathRef.current === location.pathname) return;
    lastPageViewPathRef.current = location.pathname;
    const fired = fireMetaPageView();
    debugLog('meta PageView', location.pathname, fired ? 'ok' : 'skipped');
    // Also mirror to /api/pixels/log so first-party analytics captures
    // every SPA PageView, and set the session first-touch source on
    // the entry pageview.
    try {
      if (!window.sessionStorage.getItem(SESSION_FIRSTTOUCH_KEY)) {
        const utm = captureUtmOnce() || {};
        const ref = document.referrer || null;
        // Client-side best-effort derivation mirroring the server rules.
        let src = 'direct';
        if (utm.utm_source) src = utm.utm_source.toLowerCase();
        else if (utm.fbclid) src = 'facebook_ad';
        else if (utm.twclid) src = 'twitter_ad';
        else if (utm.gclid) src = 'google_ad';
        else if (ref) src = 'referral_other';
        window.sessionStorage.setItem(SESSION_FIRSTTOUCH_KEY, src);
      }
    } catch (_) { /* noop */ }
    bufferLogEvent('PageView', {});
  }, [ready, pixels, location.pathname, isAdminEvent]);

  const value = useMemo(() => ({ pixels, trackEvent, setUserMatchData, ready }),
    [pixels, trackEvent, setUserMatchData, ready]);

  return (
    <PixelContext.Provider value={value}>
      {children}
    </PixelContext.Provider>
  );
};

export const usePixels = () => useContext(PixelContext);

// Convenience: fire an event without wiring a hook (useful in one-off
// success paths that don't want to add a hook dependency).
export function pixelTrack(event, payload) {
  // Best-effort direct fire via globals. Only works after PixelProvider
  // has booted the vendors; otherwise it's a no-op.
  debugLog('pixelTrack (global)', event, payload || {});
  try {
    if (window.fbq) {
      const method = META_STANDARD_EVENTS.has(event) ? 'track' : 'trackCustom';
      window.fbq(method, event, payload || {});
    }
    if (window.twq) window.twq('event', event, payload || {});
  } catch (e) {
    debugLog('pixelTrack failed', e);
  }
}

export default PixelContext;
